import React from 'react';
import { useEffect } from 'react';
import { Formik, Form } from 'formik';
import { Button, Row, Col } from 'reactstrap';
import validationSchema from '../AddPage/validationSchema';
import {
  callNativeAppRemoveLastRoute,
  HashimotoTestInputType,
  useTranslator,
  formatIsoDateTime,
} from '@front-activerse/common-components';
import { FormikInput as Input } from '@front-activerse/common-components';
import { formatIsoDate } from '@front-activerse/common-components';
import { useToast } from '@front-activerse/common-components';
import { callNativeMobileApp } from '@front-activerse/common-components';
import { useNavigate } from 'react-router-dom';

interface IEditTestsForm {
  editMeasurement: (request: HashimotoTestInputType) => Promise<any>;
  measurement: {
    id: number;
    type: 'tsh' | 'ft3' | 'ft4' | 'antiTg' | 'antiTpo';
    value: number;
    testedAt: string;
  };
}

export const EditTestsForm: React.FC<IEditTestsForm> = ({ editMeasurement, measurement }) => {
  const { t } = useTranslator();
  const navigate = useNavigate();

  const initialValues = {
    tsh: measurement.type === 'tsh' ? measurement.value : '',
    ft3: measurement.type === 'ft3' ? measurement.value : '',
    ft4: measurement.type === 'ft4' ? measurement.value : '',
    antiTg: measurement.type === 'antiTg' ? measurement.value : '',
    antiTpo: measurement.type === 'antiTpo' ? measurement.value : '',
    testedAt: formatIsoDate(new Date(measurement.testedAt)),
  };

  const { toastError, toastSuccess } = useToast();

  useEffect(() => {
    // trackFirebaseEvent('screen_view', {
    //   firebase_screen: 'Lab_results_add',
    //   lab_results_add: 'hashimoto',
    // });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async (values: typeof initialValues) => {
    const time = new Date().toLocaleTimeString('pl-Pl', {
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric',
    });

    const request: HashimotoTestInputType = {
      id: measurement.id,
      tsh: values.tsh === '' ? undefined : +values.tsh,
      ft3: values.ft3 === '' ? undefined : +values.ft3,
      ft4: values.ft4 === '' ? undefined : +values.ft4,
      antiTg: values.antiTg === '' ? undefined : +values.antiTg,
      antiTpo: values.antiTpo === '' ? undefined : +values.antiTpo,
      testedAt: formatIsoDateTime(new Date(values.testedAt).toISOString().slice(0, 10), time),
    };

    try {
      const response = await editMeasurement(request);

      if (response.data.hashimoto.hashimotoTestEdit.successCode === 200) {
        toastSuccess(
          <p className="mb-0 text-center">{t('labTests.notification.updatedConfirmation')}</p>
        );
        callNativeMobileApp({
          action: 'refresh_data',
          payload: {
            module: 'tests',
          },
        });
      } else {
        toastError();
      }
    } catch (error) {
      toastError();
    } finally {
      navigate(-1);
      callNativeAppRemoveLastRoute();
    }
  };

  return (
    <Formik
      validationSchema={() => validationSchema(t)}
      validateOnChange
      initialValues={initialValues}
      onSubmit={handleSubmit}
    >
      {({ values, isValid, isSubmitting }) => {
        return (
          <Form>
            {measurement.type === 'tsh' && (
              <Input name="tsh" type="number" label="TSH" unit="mIU/l" step="0.001" />
            )}
            {measurement.type === 'ft3' && (
              <Input name="ft3" type="number" label="FT3" unit="pmol/l" step="0.01" />
            )}
            {measurement.type === 'ft4' && (
              <Input name="ft4" type="number" label="FT4" unit="pmol/l" step="0.01" />
            )}
            {measurement.type === 'antiTg' && (
              <Input name="antiTg" type="number" label="anty-TG" unit="IU/ml" step="0.001" />
            )}
            {measurement.type === 'antiTpo' && (
              <Input name="antiTpo" type="number" label="anty-TPO" unit="IU/ml" step="0.001" />
            )}
            <Input name="testedAt" type="date" label="Data badań" max={formatIsoDate(new Date())} />

            <Row className="mt-xl">
              <Col xs="6" className="pe-1">
                <Button
                  color="secondary"
                  outline
                  className="w-100"
                  onClick={() => {
                    navigate(-1);
                    callNativeAppRemoveLastRoute();
                  }}
                >
                  {t('labTests.cancelButton')}
                </Button>
              </Col>
              <Col xs="6" className="ps-1">
                <Button
                  color="primary"
                  type="submit"
                  className="w-100"
                  disabled={
                    (values.tsh === '' &&
                      values.ft3 === '' &&
                      values.ft4 === '' &&
                      values.antiTg === '' &&
                      values.antiTpo === '') ||
                    !isValid ||
                    isSubmitting
                  }
                >
                  {isSubmitting ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      />
                      <span>{t('labTests.saveButton')}</span>
                    </>
                  ) : (
                    t('labTests.saveButton')
                  )}
                </Button>
              </Col>
            </Row>
          </Form>
        );
      }}
    </Formik>
  );
};

export default EditTestsForm;
